import type { Locale } from "@/context/AppProviders";

export type BlogInlineCtaVariant = {
  id: string;
  title: string;
  paragraphs: string[];
  ctaPrimary: string;
};

const VARIANTS_TR: Record<string, BlogInlineCtaVariant> = {
  default: {
    id: "default",
    title: "E-ticaret ve ön muhasebe yazılımı Enabase",
    paragraphs: [
      "Satış kanallarınız, stok, e-fatura ve cari hesaplar farklı programlarda kaldığında veri tekrarı ve hata riski artar. Enabase e-ticaret ile ön muhasebeyi aynı panelde birleştirir.",
      "KOBİ'ler ve büyüyen markalar için 30 gün ücretsiz deneyin; pazaryeri, online mağaza ve finans süreçlerinizi tek yerden yönetin.",
    ],
    ctaPrimary: "Ücretsiz Deneyin",
  },
  ecommerce: {
    id: "ecommerce",
    title: "E-ticaret ve ön muhasebeyi tek platformda yönetin",
    paragraphs: [
      "Pazaryeri siparişleri, stok, e-fatura ve online mağazanız ayrı sistemlerde dağıldığında hata kaçınılmazdır. Enabase satış kanallarını cari, kasa-banka ve belgelerle otomatik bağlar.",
      "30 gün ücretsiz deneyin; Trendyol, Hepsiburada ve kendi mağazanızı ön muhasebe ile birlikte tek panelden yönetin.",
    ],
    ctaPrimary: "Ücretsiz Deneyin",
  },
  accounting: {
    id: "accounting",
    title: "Ön muhasebe ve e-ticaret entegrasyonu Enabase",
    paragraphs: [
      "Cari hesap, kasa-banka, e-fatura ve stok takibini Excel ve dağınık programlar yerine tek panelde yönetin. Sipariş ve satış kanalları finans kayıtlarına otomatik yansır.",
      "E-ticaret yapan KOBİ'ler için ön muhasebe modülünü 30 gün ücretsiz deneyin.",
    ],
    ctaPrimary: "Ücretsiz Deneyin",
  },
  efatura: {
    id: "efatura",
    title: "e-Fatura, e-ticaret ve ön muhasebe bir arada",
    paragraphs: [
      "Siparişten e-faturaya, e-arşivden cari takibe kadar belge akışını manuel adımlar olmadan yönetin. Enabase, satış kanalları ve E-Logo gibi entegratörlerle uyumlu çalışır.",
      "e-Fatura ve ön muhasebe ihtiyaçlarınızı e-ticaret operasyonuyla birlikte 30 gün ücretsiz deneyin.",
    ],
    ctaPrimary: "Ücretsiz Deneyin",
  },
  dealer: {
    id: "dealer",
    title: "Bayi, e-ticaret ve ön muhasebe tek altyapıda",
    paragraphs: [
      "Bayilerinize özel fiyat, portal ve sipariş yönetimini perakende, pazaryeri ve muhasebe kayıtlarıyla aynı stok üzerinde birleştirin.",
      "B2B satış ağınızı Enabase ile güçlendirmek için 30 gün ücretsiz deneme başlatın.",
    ],
    ctaPrimary: "Ücretsiz Deneyin",
  },
  website: {
    id: "website",
    title: "Web sitesi, e-ticaret ve ön muhasebe tek pakette",
    paragraphs: [
      "SEO odaklı blog, kategori filtreleri ve mobil uyumlu mağazanızı Enabase Online ile kurun; satışlarınız ön muhasebe kayıtlarına otomatik aksın.",
      "Web sitesi ve finans süreçlerinizi 30 gün ücretsiz deneyin.",
    ],
    ctaPrimary: "Ücretsiz Deneyin",
  },
  pos: {
    id: "pos",
    title: "Perakende satış ve ön muhasebe tek ekranda",
    paragraphs: [
      "Mağaza kasası, barkod okuyucu ve anlık stok düşümünü e-fatura ve cari kayıtlarla aynı anda yönetin. Hızlı Satış ekranı perakende yoğunluğunu kolaylaştırır.",
      "Perakende ve e-ticaret kanallarınızı ön muhasebe ile birlikte 30 gün ücretsiz deneyin.",
    ],
    ctaPrimary: "Ücretsiz Deneyin",
  },
};

const VARIANTS_EN: Record<string, BlogInlineCtaVariant> = {
  default: {
    id: "default",
    title: "E-commerce and accounting software — Enabase",
    paragraphs: [
      "When sales channels, inventory, e-invoice, and current accounts live in separate tools, errors multiply. Enabase unifies e-commerce and accounting in one panel.",
      "Try free for 30 days — manage marketplaces, your online store, and finance from one place.",
    ],
    ctaPrimary: "Start free trial",
  },
  ecommerce: {
    id: "ecommerce",
    title: "Run e-commerce and accounting on one platform",
    paragraphs: [
      "When marketplace orders, stock, e-invoice, and your store are scattered across systems, mistakes are inevitable. Enabase connects sales channels to finance automatically.",
      "Start your 30-day free trial and manage Trendyol, Hepsiburada, and your store with accounting in one panel.",
    ],
    ctaPrimary: "Start free trial",
  },
  accounting: {
    id: "accounting",
    title: "Accounting and e-commerce integration — Enabase",
    paragraphs: [
      "Manage current accounts, cash and bank, e-invoice, and inventory in one panel. Orders and sales channels flow into financial records automatically.",
      "Try Enabase accounting with e-commerce free for 30 days.",
    ],
    ctaPrimary: "Start free trial",
  },
  efatura: {
    id: "efatura",
    title: "Speed up e-invoicing",
    paragraphs: [
      "Run document flows from order to e-invoice and e-archive without manual steps. Enabase works with E-Logo and other integrators.",
      "Try Enabase free for your e-invoice and accounting needs.",
    ],
    ctaPrimary: "Start free trial",
  },
  dealer: {
    id: "dealer",
    title: "Digitize your dealer network",
    paragraphs: [
      "Combine dealer-specific pricing, portal, and order management with retail and marketplace channels on the same stock.",
      "Start a free trial to power your B2B network with Enabase.",
    ],
    ctaPrimary: "Start free trial",
  },
  website: {
    id: "website",
    title: "Launch a professional website easily",
    paragraphs: [
      "Build your digital storefront with SEO-focused blog, category filters, and mobile-ready design through Enabase Online.",
      "Website and accounting in one package — 30-day free trial.",
    ],
    ctaPrimary: "Start free trial",
  },
  pos: {
    id: "pos",
    title: "Accelerate in-store sales",
    paragraphs: [
      "Manage POS, barcode scanning, and real-time stock deduction together with accounting entries.",
      "Discover Enabase Quick Sales with a free trial.",
    ],
    ctaPrimary: "Start free trial",
  },
};

/** Etiket slug → CTA varyantı (ilk eşleşen kazanır) */
const TAG_VARIANT_KEYS: { slugs: string[]; variant: string }[] = [
  {
    slugs: ["e-ticaret-yazilimi", "pazaryeri-entegrasyonu", "online-magaza-altapisi", "trendyol", "hepsiburada"],
    variant: "ecommerce",
  },
  {
    slugs: ["e-fatura-programi", "e-fatura", "e-arsiv", "e-fatura-nedir"],
    variant: "efatura",
  },
  {
    slugs: ["on-muhasebe-programi", "on-muhasebe-nedir", "stok-takip-yazilimi", "cari-hesap"],
    variant: "accounting",
  },
  {
    slugs: ["bayi-portali", "bayi-satis", "toptan-satis", "b2b"],
    variant: "dealer",
  },
  {
    slugs: ["web-sitesi", "seo", "blog-yonetimi"],
    variant: "website",
  },
  {
    slugs: ["hizli-satis", "pos", "perakende-satis", "kasa-yazilimi"],
    variant: "pos",
  },
];

/** Kategori slug / ad parçası → varyant */
const CATEGORY_VARIANT_PATTERNS: { pattern: RegExp; variant: string }[] = [
  { pattern: /e-?ticaret|pazaryeri|dijital.?pazarlama/i, variant: "ecommerce" },
  { pattern: /muhasebe|finans|e-?fatura|vergi/i, variant: "accounting" },
  { pattern: /bayi|toptan|b2b/i, variant: "dealer" },
  { pattern: /web|seo|site/i, variant: "website" },
];

function getVariants(locale: Locale): Record<string, BlogInlineCtaVariant> {
  return locale === "tr" ? VARIANTS_TR : VARIANTS_EN;
}

export function resolveBlogInlineCta(
  locale: Locale,
  options: {
    tagSlugs?: string[];
    categorySlug?: string;
    categoryLabel?: string;
  } = {}
): BlogInlineCtaVariant {
  const variants = getVariants(locale);
  const normalizedTags = (options.tagSlugs ?? []).map((slug) => slug.toLowerCase());

  for (const mapping of TAG_VARIANT_KEYS) {
    if (mapping.slugs.some((slug) => normalizedTags.includes(slug))) {
      return variants[mapping.variant] ?? variants.default;
    }
  }

  const categoryHaystack = `${options.categorySlug ?? ""} ${options.categoryLabel ?? ""}`.toLowerCase();
  for (const { pattern, variant } of CATEGORY_VARIANT_PATTERNS) {
    if (pattern.test(categoryHaystack)) {
      return variants[variant] ?? variants.default;
    }
  }

  return variants.default;
}
